import { useCallback, useState } from 'react';
import { useOrder } from '../contexts/OrderContext';
import { useNavigateWithContext } from './useNavigateWithContext';
import { isOrderInFlight, getLatestOrderForProduct } from '../utilities/refillRules';
import GenericConfirmationModal from '../components/GenericConfirmationModal';

export const useRefillButtonGate = () => {
  const orderContext = useOrder();
  const navigateWithContext = useNavigateWithContext();
  // { orderId, blockingOrderId } for the order awaiting confirmation, or null
  const [pendingRequest, setPendingRequest] = useState(null);

  const requestRefill = useCallback((orderId) => {
    const orders = orderContext?.orders || [];
    const targetOrder = orders.find(o => o.order_id === orderId);
    // Gate on that PRODUCT's latest order, not the patient's globally most
    // recent order — a patient can hold concurrent orders for other products.
    const blockingOrder = getLatestOrderForProduct(targetOrder, orders);
    if (isOrderInFlight(blockingOrder)) {
      setPendingRequest({ orderId, blockingOrderId: blockingOrder?.order_id });
      return;
    }
    navigateWithContext(orderId, '/refills', 'order');
  }, [orderContext?.orders, navigateWithContext]);

  const handleConfirm = useCallback(() => {
    const request = pendingRequest;
    setPendingRequest(null);
    if (request?.orderId) navigateWithContext(request.orderId, '/refills', 'order');
  }, [pendingRequest, navigateWithContext]);

  const handleCancel = useCallback(() => {
    setPendingRequest(null);
  }, []);

  const gateModal = (
    <GenericConfirmationModal
      isOpen={Boolean(pendingRequest)}
      title="Existing order in progress"
      message={`You already have an order${pendingRequest?.blockingOrderId ? ` (#${pendingRequest.blockingOrderId})` : ''} that has not been completed yet. If a recent payment showed an error, the charge may still have gone through — please contact support before submitting another refill so you are not charged twice. Are you sure you want to continue?`}
      textCancel="No, go back"
      textConfirm="Yes, continue"
      onCancel={handleCancel}
      onConfirm={handleConfirm}
    />
  );

  return { requestRefill, gateModal };
};
